import { useState, useEffect, useCallback } from 'react'
import {
    RiDeleteBinLine,
    RiFileCopyLine,
    RiBold,
    RiItalic,
    RiUnderline,
    RiStrikethrough,
    RiAlignLeft,
    RiAlignCenter,
    RiAlignRight,
    RiAlignJustify,
} from 'react-icons/ri'
import { useEditorStore } from '@/stores/useEditorStore'
import { useProjectStore } from '@/stores/useProjectStore'
import { getCanvasInstance } from '@/lib/canvasInstance'
import { FONT_LIST, loadFont } from '@/lib/fonts'
import { useIsMobile } from '@/hooks'
import styles from './PropertyPanel.module.css'

interface PropertyPanelProps {
    canvasInstance: unknown
}

interface ObjectProps {
    fill: string
    stroke: string
    strokeWidth: number
    opacity: number
    fontSize?: number
    fontFamily?: string
    fontWeight?: number | string
    fontStyle?: string
    underline?: boolean
    linethrough?: boolean
    textAlign?: string
    lineHeight?: number
    charSpacing?: number
    text?: string
    left: number
    top: number
    width: number
    height: number
    angle: number
    rx?: number
    ry?: number
    type?: string
}

export function PropertyPanel({ canvasInstance: _canvasInstance }: PropertyPanelProps) {
    const { selectedObjectIds, pushUndo } = useEditorStore()
    const { projectName, widthMm, heightMm, pages } = useProjectStore()
    const [props, setProps] = useState<ObjectProps | null>(null)
    const isMobile = useIsMobile()
    const [mobileOpen, setMobileOpen] = useState(false)

    const refreshProps = useCallback(() => {
        const canvas = getCanvasInstance()
        if (!canvas) return

        const active = canvas.getActiveObject()
        if (!active) {
            setProps(null)
            return
        }

        setProps({
            fill: (active.fill as string) || '#000000',
            stroke: (active.stroke as string) || '',
            strokeWidth: active.strokeWidth || 0,
            opacity: active.opacity ?? 1,
            fontSize: active.fontSize,
            fontFamily: active.fontFamily,
            fontWeight: active.fontWeight,
            fontStyle: active.fontStyle,
            underline: active.underline,
            linethrough: active.linethrough,
            textAlign: active.textAlign,
            lineHeight: active.lineHeight,
            charSpacing: active.charSpacing,
            text: active.text,
            left: Math.round(active.left || 0),
            top: Math.round(active.top || 0),
            width: Math.round((active.width || 0) * (active.scaleX || 1)),
            height: Math.round((active.height || 0) * (active.scaleY || 1)),
            angle: Math.round(active.angle || 0),
            rx: active.rx,
            ry: active.ry,
            type: active.type,
        })
    }, [])

    useEffect(() => {
        refreshProps()
    }, [selectedObjectIds, refreshProps])

    useEffect(() => {
        if (isMobile) {
            setMobileOpen(selectedObjectIds.length > 0)
        }
    }, [selectedObjectIds, isMobile])

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateProp = useCallback((key: string, value: any) => {
        const canvas = getCanvasInstance()
        if (!canvas) return
        const active = canvas.getActiveObject()
        if (!active) return

        active.set(key, value)
        canvas.requestRenderAll()
        refreshProps()
        useEditorStore.getState().setIsDirty(true)
    }, [refreshProps])

    const updateSize = useCallback((dimension: 'width' | 'height', newValue: number) => {
        const canvas = getCanvasInstance()
        if (!canvas) return
        const active = canvas.getActiveObject()
        if (!active) return

        if (dimension === 'width' && active.width) {
            active.set('scaleX', newValue / active.width)
        } else if (dimension === 'height' && active.height) {
            active.set('scaleY', newValue / active.height)
        }
        active.setCoords()
        canvas.requestRenderAll()
        refreshProps()
        useEditorStore.getState().setIsDirty(true)
    }, [refreshProps])

    const handleFontChange = useCallback(async (family: string) => {
        await loadFont(family)
        updateProp('fontFamily', family)
    }, [updateProp])

    const handleDelete = useCallback(() => {
        const canvas = getCanvasInstance()
        if (!canvas) return
        const active = canvas.getActiveObjects()
        if (!active.length) return
        pushUndo(JSON.stringify(canvas.toJSON()))
        active.forEach((obj: unknown) => canvas.remove(obj))
        canvas.discardActiveObject()
        canvas.requestRenderAll()
    }, [pushUndo])

    const handleDuplicate = useCallback(async () => {
        const canvas = getCanvasInstance()
        if (!canvas) return
        const active = canvas.getActiveObject()
        if (!active) return
        pushUndo(JSON.stringify(canvas.toJSON()))
        const cloned = await active.clone()
        cloned.set({ left: (active.left || 0) + 20, top: (active.top || 0) + 20 })
        canvas.add(cloned)
        canvas.setActiveObject(cloned)
        canvas.requestRenderAll()
    }, [pushUndo])

    const isText = props !== null && props.fontSize !== undefined
    const isRect = props !== null && props.type === 'rect'

    // Panel content is identical on mobile and desktop — only the wrapper changes
    const panelContent = (selectedObjectIds.length === 0 || !props) ? (
        <>
            <h3 className={styles.title}>Documento</h3>
            <div className={styles.section}>
                <div className={styles.docInfo}>
                    <span className={styles.docName}>{projectName || 'Sin nombre'}</span>
                    <span className={styles.docMeta}>{widthMm} × {heightMm} mm</span>
                    <span className={styles.docMeta}>{pages.length} página{pages.length !== 1 ? 's' : ''}</span>
                </div>
            </div>
            <p className={styles.placeholder}>Selecciona un elemento para editar sus propiedades</p>
        </>
    ) : (
        <>
            <h3 className={styles.title}>Propiedades</h3>

            {/* Quick actions */}
            <div className={styles.quickActions}>
                <button className={styles.actionBtn} onClick={handleDuplicate} title="Duplicar (Ctrl+D)">
                    <RiFileCopyLine />
                </button>
                <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={handleDelete} title="Eliminar">
                    <RiDeleteBinLine />
                </button>
            </div>

            {/* Position & Size */}
            <div className={styles.section}>
                <label className={styles.sectionLabel}>Posición y tamaño</label>
                <div className={styles.row}>
                    <div className={styles.inputGroup}>
                        <span className={styles.inputLabel}>X</span>
                        <input type="number" className={styles.input} value={props.left}
                            onChange={(e) => updateProp('left', Number(e.target.value))} />
                    </div>
                    <div className={styles.inputGroup}>
                        <span className={styles.inputLabel}>Y</span>
                        <input type="number" className={styles.input} value={props.top}
                            onChange={(e) => updateProp('top', Number(e.target.value))} />
                    </div>
                </div>
                <div className={styles.row}>
                    <div className={styles.inputGroup}>
                        <span className={styles.inputLabel}>W</span>
                        <input type="number" className={styles.input} value={props.width}
                            onChange={(e) => updateSize('width', Number(e.target.value))} />
                    </div>
                    <div className={styles.inputGroup}>
                        <span className={styles.inputLabel}>H</span>
                        <input type="number" className={styles.input} value={props.height}
                            onChange={(e) => updateSize('height', Number(e.target.value))} />
                    </div>
                </div>
                <div className={styles.row}>
                    <div className={styles.inputGroup}>
                        <span className={styles.inputLabel}>R</span>
                        <input type="number" className={styles.input} value={props.angle}
                            onChange={(e) => updateProp('angle', Number(e.target.value))} />
                    </div>
                    {isRect && (
                        <div className={styles.inputGroup}>
                            <span className={styles.inputLabel}>⌒</span>
                            <input type="number" className={styles.input} value={props.rx || 0}
                                onChange={(e) => { updateProp('rx', Number(e.target.value)); updateProp('ry', Number(e.target.value)) }} />
                        </div>
                    )}
                </div>
            </div>

            {/* Text formatting */}
            {isText && (
                <div className={styles.section}>
                    <label className={styles.sectionLabel}>Texto</label>
                    {/* Font family */}
                    <select className={styles.select} value={props.fontFamily || 'Arial'}
                        onChange={(e) => handleFontChange(e.target.value)}>
                        {FONT_LIST.map(f => (
                            <option key={f.family} value={f.family}>{f.family}</option>
                        ))}
                    </select>
                    {/* Size + Weight */}
                    <div className={styles.row}>
                        <div className={styles.inputGroup}>
                            <span className={styles.inputLabel}>Px</span>
                            <input type="number" className={styles.input} value={props.fontSize}
                                onChange={(e) => updateProp('fontSize', Number(e.target.value))} />
                        </div>
                        <select className={styles.selectSm} value={props.fontWeight || 400}
                            onChange={(e) => updateProp('fontWeight', e.target.value)}>
                            <option value="300">Light</option>
                            <option value="400">Regular</option>
                            <option value="500">Medium</option>
                            <option value="600">SemiBold</option>
                            <option value="700">Bold</option>
                            <option value="800">ExtraBold</option>
                        </select>
                    </div>
                    {/* Style toggles */}
                    <div className={styles.toggleRow}>
                        <button className={`${styles.toggleBtn} ${props.fontStyle === 'italic' ? styles.toggleActive : ''}`}
                            onClick={() => updateProp('fontStyle', props.fontStyle === 'italic' ? 'normal' : 'italic')} title="Cursiva">
                            <RiItalic />
                        </button>
                        <button className={`${styles.toggleBtn} ${Number(props.fontWeight) >= 700 ? styles.toggleActive : ''}`}
                            onClick={() => updateProp('fontWeight', Number(props.fontWeight) >= 700 ? '400' : '700')} title="Negrita">
                            <RiBold />
                        </button>
                        <button className={`${styles.toggleBtn} ${props.underline ? styles.toggleActive : ''}`}
                            onClick={() => updateProp('underline', !props.underline)} title="Subrayado">
                            <RiUnderline />
                        </button>
                        <button className={`${styles.toggleBtn} ${props.linethrough ? styles.toggleActive : ''}`}
                            onClick={() => updateProp('linethrough', !props.linethrough)} title="Tachado">
                            <RiStrikethrough />
                        </button>
                    </div>
                    {/* Alignment */}
                    <div className={styles.toggleRow}>
                        {(['left', 'center', 'right', 'justify'] as const).map(align => {
                            const Icon = { left: RiAlignLeft, center: RiAlignCenter, right: RiAlignRight, justify: RiAlignJustify }[align]
                            return (
                                <button key={align}
                                    className={`${styles.toggleBtn} ${props.textAlign === align ? styles.toggleActive : ''}`}
                                    onClick={() => updateProp('textAlign', align)}
                                    title={align.charAt(0).toUpperCase() + align.slice(1)}>
                                    <Icon />
                                </button>
                            )
                        })}
                    </div>
                    {/* Spacing */}
                    <div className={styles.row}>
                        <div className={styles.inputGroup}>
                            <span className={styles.inputLabel} title="Interlineado">↕</span>
                            <input type="number" className={styles.input} step="0.1"
                                value={Math.round((props.lineHeight || 1.16) * 100) / 100}
                                onChange={(e) => updateProp('lineHeight', Number(e.target.value))} />
                        </div>
                        <div className={styles.inputGroup}>
                            <span className={styles.inputLabel} title="Espaciado">↔</span>
                            <input type="number" className={styles.input} step="10"
                                value={props.charSpacing || 0}
                                onChange={(e) => updateProp('charSpacing', Number(e.target.value))} />
                        </div>
                    </div>
                </div>
            )}

            {/* Colors */}
            <div className={styles.section}>
                <label className={styles.sectionLabel}>Color</label>
                <div className={styles.colorRow}>
                    <span className={styles.colorLabel}>Relleno</span>
                    <div className={styles.colorGroup}>
                        <input type="color" className={styles.colorInput}
                            value={typeof props.fill === 'string' ? props.fill : '#000000'}
                            onChange={(e) => updateProp('fill', e.target.value)} />
                        <button className={styles.colorClear} onClick={() => updateProp('fill', 'transparent')} title="Sin relleno">×</button>
                    </div>
                </div>
                <div className={styles.colorRow}>
                    <span className={styles.colorLabel}>Borde</span>
                    <div className={styles.colorGroup}>
                        <input type="color" className={styles.colorInput}
                            value={props.stroke || '#000000'}
                            onChange={(e) => updateProp('stroke', e.target.value)} />
                        <input type="number" className={styles.inputXs} value={props.strokeWidth}
                            onChange={(e) => updateProp('strokeWidth', Number(e.target.value))} min={0} max={20} />
                    </div>
                </div>
            </div>

            {/* Opacity */}
            <div className={styles.section}>
                <label className={styles.sectionLabel}>Opacidad</label>
                <div className={styles.opacityRow}>
                    <input type="range" min="0" max="1" step="0.05" value={props.opacity}
                        onChange={(e) => updateProp('opacity', Number(e.target.value))}
                        className={styles.range} />
                    <span className={styles.rangeValue}>{Math.round(props.opacity * 100)}%</span>
                </div>
            </div>
        </>
    )

    if (isMobile) {
        return (
            <>
                {mobileOpen && (
                    <div className={styles.backdrop} onClick={() => setMobileOpen(false)} />
                )}
                <div className={`${styles.panel} ${styles.mobilePanel} ${mobileOpen ? styles.mobilePanelVisible : ''}`}>
                    <div className={styles.dragHandle}>
                        <div className={styles.dragBar} />
                    </div>
                    {panelContent}
                </div>
            </>
        )
    }

    return (
        <div className={styles.panel}>
            {panelContent}
        </div>
    )
}
